import React from "react";
import { BRAND, IMAGES } from "@/content";

/**
 * Presentación del producto.
 * Si en content.js cargan IMAGES.productoBolsa / productoStick se usan esas fotos.
 * Si no, se dibuja una representación del empaque con la paleta de marca.
 */

function PouchFallback() {
  return (
    <div
      className="relative select-none"
      style={{
        width: "min(64vw, 262px)",
        aspectRatio: "268 / 340",
        borderRadius: 24,
        background: "linear-gradient(158deg, #7c2745 0%, #611B34 48%, #45111F 100%)",
        boxShadow: "0 30px 60px rgba(74,20,40,0.4), inset 0 1px 0 rgba(255,255,255,0.22)",
        overflow: "hidden",
      }}
      aria-hidden="true"
    >
      {/* brillo lateral */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          background:
            "linear-gradient(105deg, rgba(255,255,255,0.14) 0%, rgba(255,255,255,0.03) 34%, rgba(255,255,255,0) 55%)",
        }}
      />
      {/* sello superior */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: 26,
          background: "rgba(255,255,255,0.10)",
          borderBottom: "1px dashed rgba(255,255,255,0.35)",
        }}
      />

      <div className="relative h-full flex flex-col items-center px-5 pt-10 pb-5 text-center">
        <img
          src={BRAND.isotipoFuxionBlanco}
          alt=""
          className="h-6 w-auto opacity-95"
          loading="lazy"
        />
        <div
          className="font-display text-white mt-5"
          style={{ fontSize: 38, lineHeight: 0.95, letterSpacing: "-0.03em" }}
        >
          PRUNEX
        </div>
        <div
          className="font-display"
          style={{ color: "var(--lima)", fontSize: 60, lineHeight: 0.9 }}
        >
          1
        </div>
        <div
          className="mt-2 rounded-full px-3 py-1 text-[11px] font-semibold"
          style={{ background: "var(--lima)", color: "#3d5c16", letterSpacing: "0.06em" }}
        >
          FIBRA + PREBIÓTICO
        </div>
        <div className="mt-auto w-full">
          <div
            className="text-white/90 text-[12px] font-semibold"
            style={{ letterSpacing: "0.14em" }}
          >
            SABOR GUINDÓN
          </div>
          <div
            className="mt-2 pt-2 text-white/60 text-[11px]"
            style={{ borderTop: "1px solid rgba(255,255,255,0.2)", letterSpacing: "0.12em" }}
          >
            28 STICKS · BEBIDA INSTANTÁNEA
          </div>
        </div>
      </div>
    </div>
  );
}

function StickFallback() {
  return (
    <div
      className="select-none"
      style={{
        width: 62,
        height: 196,
        borderRadius: 8,
        background: "linear-gradient(150deg, #9a3055 0%, #6B1F3A 60%, #4A1428 100%)",
        boxShadow: "0 16px 34px rgba(74,20,40,0.34)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "10px 4px",
        border: "1px solid rgba(255,255,255,0.14)",
      }}
      aria-hidden="true"
    >
      <div
        style={{
          width: "100%",
          height: 8,
          background: "rgba(255,255,255,0.16)",
          borderRadius: 3,
        }}
      />
      <div
        className="font-display text-white"
        style={{
          writingMode: "vertical-rl",
          fontSize: 15,
          letterSpacing: "0.06em",
        }}
      >
        PRUNEX <span style={{ color: "var(--lima)" }}>1</span>
      </div>
      <div
        style={{
          width: "100%",
          height: 8,
          background: "rgba(139,197,63,0.85)",
          borderRadius: 3,
        }}
      />
    </div>
  );
}

export default function ProductShot() {
  return (
    <div
      className="relative flex items-center justify-center"
      style={{ minHeight: 420 }}
    >
      {/* blob circular en degradado rosa → lima */}
      <div
        className="absolute rounded-full"
        style={{
          width: "min(86vw, 420px)",
          height: "min(86vw, 420px)",
          background:
            "radial-gradient(circle at 34% 28%, #FCE4EC 0%, #F7EFD9 48%, #E3F2C8 100%)",
          zIndex: 0,
        }}
        aria-hidden="true"
      />

      {/* decorativos: ciruelas y anís estrella */}
      <img
        src={IMAGES.ciruelas}
        alt=""
        loading="lazy"
        className="absolute rounded-full object-cover floating"
        style={{
          width: 68,
          height: 68,
          top: "13%",
          left: "1%",
          boxShadow: "0 12px 28px rgba(107,31,58,0.26)",
          zIndex: 5,
          border: "3px solid #fff",
          animationDelay: "0.4s",
        }}
        aria-hidden="true"
      />
      <img
        src={IMAGES.anis}
        alt=""
        loading="lazy"
        className="absolute rounded-full object-cover floating"
        style={{
          width: 58,
          height: 58,
          bottom: "6%",
          left: "9%",
          boxShadow: "0 12px 28px rgba(107,31,58,0.26)",
          zIndex: 5,
          border: "3px solid #fff",
          animationDelay: "1.6s",
        }}
        aria-hidden="true"
      />

      {/* producto */}
      <div className="relative z-[3] w-full flex items-center justify-center">
        {IMAGES.productoBolsa ? (
          <img
            src={IMAGES.productoBolsa}
            alt="Bolsa de Prunex 1 de FuXion con 28 sticks, sabor guindón"
            className="w-[min(58vw,248px)] h-auto relative z-[3]"
            style={{ filter: "drop-shadow(0 26px 40px rgba(74,20,40,0.28))" }}
          />
        ) : (
          <div className="flex items-end justify-center">
            <PouchFallback />
            <div
              className="-ml-6 mb-2"
              style={{ transform: "rotate(9deg)", transformOrigin: "bottom center" }}
            >
              <StickFallback />
            </div>
          </div>
        )}

        {IMAGES.productoStick && (
          <img
            src={IMAGES.productoStick}
            alt="Stick individual de Prunex 1"
            className="absolute z-[4] floating"
            style={{
              width: "min(44vw, 205px)",
              bottom: "13%",
              right: "5%",
              transform: "rotate(-13deg)",
              filter: "drop-shadow(0 16px 26px rgba(74,20,40,0.26))",
              animationDelay: "0.9s",
            }}
          />
        )}
      </div>

      {/* badge circular 28 STICKS */}
      <div
        className="absolute rounded-full flex flex-col items-center justify-center text-white"
        style={{
          width: 78,
          height: 78,
          top: "2%",
          right: "5%",
          background: "var(--vino)",
          boxShadow: "0 14px 30px rgba(107,31,58,0.35)",
          zIndex: 4,
          border: "3px solid #fff",
        }}
      >
        <span className="font-display" style={{ fontSize: 28, lineHeight: 1 }}>
          28
        </span>
        <span style={{ fontSize: 10, letterSpacing: "0.14em", fontWeight: 600 }}>
          STICKS
        </span>
      </div>
    </div>
  );
}
